import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";

type Kind = "certificate" | "prescription" | "referral" | "summary" | "report";

type Issued = {
  id: string;
  kind: Kind;
  reference: string;
  issued_at: string;
  status: "active" | "cancelled";
  cancelled_at: string | null;
  cancellation_reason: string;
  patient_name: string;
  patient_identifier: string;
  doctor_name: string;
  doctor_registration_number: string;
  clinic_name: string;
  clinic_address: string;
  clinic_phone: string;
  clinic_registration_number: string;
  detail: Record<string, unknown>;
};

const TITLES: Record<Kind, string> = {
  certificate: "Medical Certificate",
  prescription: "Prescription",
  referral: "Referral Letter",
  summary: "Summary of Your Visit",
  report: "Medical Report",
};

function longDate(value: string) {
  return new Date(value).toLocaleDateString("en-MY", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * The page a doctor prints and hands over.
 *
 * Everything on it comes off the document's own row. The letterhead is the one
 * captured when it was issued, not the clinic as it stands today, which is why
 * a certificate reprinted in two years still reads correctly (ADR-016 point 5).
 *
 * v1 has no PDF renderer. The browser prints this, and the print stylesheet in
 * styles.css strips the portal chrome (ADR-016 point 2).
 *
 * A cancelled document still renders. It carries a cancelled mark across it,
 * because the patient may be holding the original and somebody has to be able
 * to check it (CLN-F-088).
 */
export function PrintableDocument() {
  const { kind, id } = useParams<{ kind: Kind; id: string }>();
  const [doc, setDoc] = useState<Issued | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!kind || !id) return;
    void (async () => {
      const { data, error: failed } = await api.GET("/portal/documents/issued/{id}", {
        params: { path: { id }, query: { kind }, header: clinicHeader() },
      });
      if (failed) {
        setError(readError(failed).message);
        return;
      }
      setDoc(data as Issued);
    })();
  }, [kind, id]);

  if (error) return <ErrorNote message={error} />;
  if (!doc) return <p className="empty">Reading the document…</p>;

  const d = doc.detail ?? {};

  return (
    <article className="sheet">
      <div className="sheet-actions no-print">
        <button type="button" onClick={() => window.print()}>
          Print
        </button>
        <span className="muted">Print onto the clinic's letterhead, or use this one.</span>
      </div>

      {doc.status === "cancelled" && (
        <p className="sheet-void">
          Cancelled {doc.cancelled_at ? `on ${longDate(doc.cancelled_at)}` : ""}
          {doc.cancellation_reason ? ` · ${doc.cancellation_reason}` : ""}
        </p>
      )}

      <header className="sheet-head">
        <h1>{doc.clinic_name}</h1>
        {doc.clinic_address && <p>{doc.clinic_address}</p>}
        <p>
          {doc.clinic_phone && <>Tel {doc.clinic_phone} · </>}
          Reg. {doc.clinic_registration_number}
        </p>
      </header>

      <h2 className="sheet-title">{TITLES[doc.kind]}</h2>

      <dl className="sheet-meta">
        <dt>Reference</dt>
        <dd>{doc.reference}</dd>
        <dt>Date</dt>
        <dd>{longDate(doc.issued_at)}</dd>
        <dt>Patient</dt>
        <dd>{doc.patient_name}</dd>
        {/* A Malaysian employer reads this off the certificate to know which
            person it is about (ADR-019 point 7). */}
        {doc.patient_identifier && (
          <>
            <dt>Identity number</dt>
            <dd className="mono">{doc.patient_identifier}</dd>
          </>
        )}
      </dl>

      {doc.kind === "certificate" && (
        <div className="sheet-body">
          <p>
            This is to certify that <strong>{doc.patient_name}</strong> was examined and is unfit
            for duty for <strong>{String(d.days)} day(s)</strong>, from{" "}
            <strong>{longDate(String(d.start_date))}</strong> to{" "}
            <strong>{longDate(String(d.end_date))}</strong>, both days inclusive.
          </p>
          {Boolean(d.remarks) && <p>{String(d.remarks)}</p>}
        </div>
      )}

      {doc.kind === "prescription" && (
        <div className="sheet-body">
          <table className="sheet-table">
            <thead>
              <tr>
                <th>Medicine</th>
                <th>Dosage</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {((d.lines as Record<string, string>[]) ?? []).map((line, i) => (
                <tr key={i}>
                  <td>{line.medication_text}</td>
                  <td>{line.dosage_text}</td>
                  <td>{line.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {Boolean(d.notes) && <p>{String(d.notes)}</p>}
        </div>
      )}

      {doc.kind === "referral" && (
        <div className="sheet-body">
          <p>
            <strong>To:</strong> {String(d.addressee)}
            {Boolean(d.speciality) && ` (${String(d.speciality)})`}
          </p>
          {d.urgency === "urgent" && <p className="sheet-urgent">Urgent</p>}
          <p>
            <strong>Re:</strong> {doc.patient_name}
          </p>
          <p>{String(d.reason_text)}</p>
          {Boolean(d.clinical_summary) && <p>{String(d.clinical_summary)}</p>}
          <p>Thank you for seeing this patient.</p>
        </div>
      )}

      {doc.kind === "summary" && (
        <div className="sheet-body">
          <p className="sheet-prose">{String(d.what_happened ?? "")}</p>
          {Boolean(d.what_next) && (
            <>
              <p>
                <strong>What happens next</strong>
              </p>
              <p className="sheet-prose">{String(d.what_next)}</p>
            </>
          )}
        </div>
      )}

      {doc.kind === "report" && (
        <div className="sheet-body">
          <p>
            <strong>To:</strong> {String(d.addressee)}
          </p>
          <p>
            <strong>Purpose:</strong> {String(d.purpose)}
          </p>
          <p>
            <strong>Re:</strong> {doc.patient_name}
          </p>
          <p className="sheet-prose">{String(d.body ?? "")}</p>
        </div>
      )}

      <footer className="sheet-sign">
        <div className="sheet-rule" />
        <p>
          <strong>{doc.doctor_name}</strong>
        </p>
        <p>Reg. {doc.doctor_registration_number}</p>
      </footer>
    </article>
  );
}
